import { supabase } from '@/integrations/supabase/client';

export interface ContextualSuggestion {
  id: string;
  title: string;
  description: string;
  icon: string;
  question: string;
  priority: number;
}

export interface DocumentContext {
  documentType: string;
  summary: string;
  keyData: {
    names: string[];
    values: string[];
    dates: string[];
    processNumbers: string[];
    parties: string[];
  };
}

/**
 * Generates contextual suggestions based on document analysis
 */
export async function generateContextualSuggestions(
  summary: string, 
  documentType: string,
  extractedText: string
): Promise<ContextualSuggestion[]> {
  console.log('🔍 Generating contextual suggestions for document type:', documentType);
  
  // Extract key data from the document - moved outside try block to fix scope
  const keyData = extractKeyDocumentData(extractedText);
  
  try {
    // Create context-aware prompt for AI
    const contextualPrompt = buildContextualPrompt(summary, documentType, keyData);
    
    // Call AI to generate specific suggestions - updated to use GPT-4.1
    const { data, error } = await supabase.functions.invoke('openai-chat', {
      body: {
        messages: [
          {
            role: 'system',
            content: 'Assistente jurídico especializado. Gere 5 perguntas específicas baseadas no documento. Use dados reais. Responda APENAS JSON array: title, question, icon, priority.'
          },
          {
            role: 'user',
            content: contextualPrompt
          }
        ],
        model: 'gpt-4.1-2025-04-14', // Changed from gpt-4o-mini to gpt-4.1-2025-04-14
        auto_summarize: false
      }
    });

    if (error) {
      console.error('Error generating contextual suggestions:', error);
      return generateFallbackSuggestions(documentType, keyData);
    }

    try {
      const aiResponse = data?.content || data?.response || '';
      const parsedSuggestions = JSON.parse(aiResponse);
      
      if (Array.isArray(parsedSuggestions)) {
        return parsedSuggestions.map((suggestion, index) => ({
          id: `contextual-${index}`,
          title: suggestion.title || `Pergunta ${index + 1}`,
          description: 'Pergunta específica sobre este documento',
          icon: suggestion.icon || '💭',
          question: suggestion.question || suggestion.title,
          priority: suggestion.priority || index + 1
        }));
      }
    } catch (parseError) {
      console.error('Error parsing AI suggestions:', parseError);
    }
    
    return generateFallbackSuggestions(documentType, keyData);
  } catch (error) {
    console.error('Error generating contextual suggestions:', error);
    return generateFallbackSuggestions(documentType, keyData);
  }
}

/**
 * Extracts key data from document text
 */
function extractKeyDocumentData(text: string): DocumentContext['keyData'] {
  const upperText = text.toUpperCase();
  
  // Extract names (patterns for legal names)
  const names = extractNames(text);
  
  // Extract monetary values
  const valueMatches = text.match(/R\$\s*[\d.,]+/gi) || [];
  const values = [...new Set(valueMatches)].slice(0, 5);
  
  // Extract dates
  const dateMatches = text.match(/\d{1,2}\/\d{1,2}\/\d{4}/g) || [];
  const dates = [...new Set(dateMatches)].slice(0, 5);
  
  // Extract process numbers
  const processMatches = text.match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/g) || [];
  const processNumbers = [...new Set(processMatches)];
  
  // Extract parties (Reclamante, Reclamado, etc.)
  const parties: string[] = [];
  const reclamanteMatch = text.match(/reclamante:?\s*([^,\n\.]+)/i);
  const reclamadoMatch = text.match(/reclamad[oa]:?\s*([^,\n\.]+)/i);
  const autorMatch = text.match(/autor:?\s*([^,\n\.]+)/i);
  const reuMatch = text.match(/r[eé]u:?\s*([^,\n\.]+)/i);
  
  if (reclamanteMatch) parties.push(`Reclamante: ${reclamanteMatch[1].trim()}`);
  if (reclamadoMatch) parties.push(`Reclamado: ${reclamadoMatch[1].trim()}`);
  if (autorMatch) parties.push(`Autor: ${autorMatch[1].trim()}`);
  if (reuMatch) parties.push(`Réu: ${reuMatch[1].trim()}`);
  
  return {
    names: names.slice(0, 5),
    values,
    dates,
    processNumbers,
    parties
  };
}

/**
 * Extract names from text (same logic as documentAnalysisService)
 */
function extractNames(text: string): string[] {
  const lines = text.split('\n').map(line => line.trim());
  const names: string[] = [];
  
  for (const line of lines) {
    if (line.match(/^[A-ZÁÊÇÕ][a-záêçõ]+(?:\s+[A-ZÁÊÇÕ][a-záêçõ]+)+$/)) {
      if (!line.includes('@') && !line.match(/\d/) && line.length > 5 && line.length < 50) {
        names.push(line);
      }
    }
  }
  
  return [...new Set(names)].slice(0, 5);
}

/**
 * Builds contextual prompt for AI
 */
function buildContextualPrompt(summary: string, documentType: string, keyData: DocumentContext['keyData']): string {
  const context = {
    tipo: documentType,
    resumo: summary,
    nomes: keyData.names,
    valores: keyData.values,
    datas: keyData.dates,
    processos: keyData.processNumbers,
    partes: keyData.parties
  };
  
  return `Baseado neste documento jurídico, gere 5 perguntas específicas e práticas:

CONTEXTO DO DOCUMENTO:
${JSON.stringify(context, null, 2)}

INSTRUÇÕES:
1. Use os nomes, valores e datas REAIS extraídos do documento
2. Faça perguntas específicas sobre ESTE documento específico
3. Seja prático e objetivo
4. Use emojis apropriados para cada pergunta
5. Priorize por relevância (1 = mais importante)

FORMATO ESPERADO (JSON):
[
  {
    "title": "💰 Calcular verbas devidas a [NOME_REAL]",
    "question": "Com base no processo de [NOME_RECLAMANTE] contra [NOME_RECLAMADO], calcule as verbas trabalhistas devidas considerando o valor de [VALOR_REAL] mencionado.",
    "icon": "💰",
    "priority": 1
  }
]`;
}

/**
 * Generates fallback suggestions when AI fails
 */
function generateFallbackSuggestions(documentType: string, keyData: DocumentContext['keyData']): ContextualSuggestion[] {
  const suggestions: ContextualSuggestion[] = [];
  const mainParty = keyData.names[0] || keyData.parties[0] || 'a parte interessada';
  const mainValue = keyData.values[0] || 'os valores';
  
  // Base suggestions based on document type
  if (documentType.includes('TRABALHISTA') || documentType.includes('TRABALHO')) {
    suggestions.push(
      {
        id: 'calc-verbas',
        title: `💰 Calcular verbas devidas`,
        description: 'Cálculo detalhado das verbas trabalhistas',
        icon: '💰',
        question: keyData.names.length > 0 
          ? `Calcular as verbas trabalhistas devidas para ${keyData.names[0]} baseado nos dados do processo.`
          : 'Calcular as verbas trabalhistas devidas baseado nos dados do documento enviado.',
        priority: 1
      },
      {
        id: 'analise-chances',
        title: '📊 Analisar chances de êxito',
        description: 'Avaliação das chances de sucesso',
        icon: '📊',
        question: `Analisar as chances de êxito neste processo trabalhista baseado na documentação apresentada.`,
        priority: 2
      }
    );
  } else if (documentType.includes('CIVIL') || documentType.includes('PROCESSO')) {
    suggestions.push(
      {
        id: 'defesa-estrategia',
        title: '⚖️ Elaborar estratégia de defesa',
        description: 'Estratégia jurídica para o caso',
        icon: '⚖️',
        question: `Elaborar uma estratégia de defesa para este processo baseado nos fatos apresentados.`,
        priority: 1
      }
    );
  }
  
  // Universal suggestions with context
  suggestions.push(
    {
      id: 'prazos-importantes',
      title: '📅 Verificar prazos importantes',
      description: 'Identificar prazos processuais críticos',
      icon: '📅',
      question: `Identificar os prazos processuais importantes e próximos vencimentos neste caso.`,
      priority: 3
    },
    {
      id: 'documentos-complementares',
      title: '📋 Documentos complementares necessários',
      description: 'Listar documentos adicionais recomendados',
      icon: '📋',
      question: `Que documentos complementares seriam necessários para fortalecer este caso?`,
      priority: 4
    },
    {
      id: 'analise-juridica',
      title: '🔍 Análise jurídica detalhada',
      description: 'Análise completa dos aspectos jurídicos',
      icon: '🔍',
      question: `Fazer uma análise jurídica detalhada deste documento, destacando pontos fortes e fracos.`,
      priority: 5
    }
  );
  
  return suggestions.slice(0, 5); // Limit to 5 suggestions
}
